// Synchronous SHA-256 (FIPS 180-4), so buildAtlas hashes identically in the browser and in Node
// (tests/art checks it against node:crypto). Incremental: update(bytes)… then hex().

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class Sha256 {
  #h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  #buf = new Uint8Array(64);
  #bufLen = 0;
  #bytes = 0;
  #w = new Uint32Array(64);

  update(data: Uint8Array | Uint8ClampedArray | string): this {
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    this.#bytes += bytes.length;
    let i = 0;
    if (this.#bufLen > 0) {
      while (i < bytes.length && this.#bufLen < 64) this.#buf[this.#bufLen++] = bytes[i++] ?? 0;
      if (this.#bufLen === 64) {
        this.#block(this.#buf, 0);
        this.#bufLen = 0;
      }
    }
    for (; i + 64 <= bytes.length; i += 64) this.#block(bytes, i);
    while (i < bytes.length) this.#buf[this.#bufLen++] = bytes[i++] ?? 0;
    return this;
  }

  hex(): string {
    const bits = this.#bytes * 8;
    const pad = new Uint8Array((this.#bufLen < 56 ? 56 : 120) - this.#bufLen + 8);
    pad[0] = 0x80;
    const hi = Math.floor(bits / 0x100000000);
    const lo = bits >>> 0;
    const n = pad.length;
    pad[n - 8] = (hi >>> 24) & 255;
    pad[n - 7] = (hi >>> 16) & 255;
    pad[n - 6] = (hi >>> 8) & 255;
    pad[n - 5] = hi & 255;
    pad[n - 4] = (lo >>> 24) & 255;
    pad[n - 3] = (lo >>> 16) & 255;
    pad[n - 2] = (lo >>> 8) & 255;
    pad[n - 1] = lo & 255;
    const total = this.#bytes;
    this.update(pad);
    this.#bytes = total;
    let out = '';
    for (const v of this.#h) out += v.toString(16).padStart(8, '0');
    return out;
  }

  #block(b: ArrayLike<number>, o: number): void {
    const w = this.#w;
    for (let t = 0; t < 16; t++) {
      const j = o + t * 4;
      w[t] =
        (((b[j] ?? 0) << 24) |
          ((b[j + 1] ?? 0) << 16) |
          ((b[j + 2] ?? 0) << 8) |
          (b[j + 3] ?? 0)) >>>
        0;
    }
    for (let t = 16; t < 64; t++) {
      const x = w[t - 15] ?? 0;
      const y = w[t - 2] ?? 0;
      const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
      const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
      w[t] = ((w[t - 16] ?? 0) + s0 + (w[t - 7] ?? 0) + s1) >>> 0;
    }
    const h = this.#h;
    let a = h[0] ?? 0;
    let bb = h[1] ?? 0;
    let c = h[2] ?? 0;
    let d = h[3] ?? 0;
    let e = h[4] ?? 0;
    let f = h[5] ?? 0;
    let g = h[6] ?? 0;
    let hh = h[7] ?? 0;
    for (let t = 0; t < 64; t++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + (K[t] ?? 0) + (w[t] ?? 0)) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & bb) ^ (a & c) ^ (bb & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = bb;
      bb = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = ((h[0] ?? 0) + a) >>> 0;
    h[1] = ((h[1] ?? 0) + bb) >>> 0;
    h[2] = ((h[2] ?? 0) + c) >>> 0;
    h[3] = ((h[3] ?? 0) + d) >>> 0;
    h[4] = ((h[4] ?? 0) + e) >>> 0;
    h[5] = ((h[5] ?? 0) + f) >>> 0;
    h[6] = ((h[6] ?? 0) + g) >>> 0;
    h[7] = ((h[7] ?? 0) + hh) >>> 0;
  }
}

export function sha256Hex(...parts: (Uint8Array | Uint8ClampedArray | string)[]): string {
  const s = new Sha256();
  for (const p of parts) s.update(p);
  return s.hex();
}
